// src/services/api.js
import apiClient from './apiClient';

export default {
  // === Admin API ===

  // Points Management
  getPointsSummary: () => apiClient.get('/admin/points/summary'),
  
  getPointsUsers: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    // Add pagination and filters
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.partnerId) queryParams.append('partnerId', params.partnerId);
    if (params.status) queryParams.append('status', params.status);
    if (params.minBalance) queryParams.append('minBalance', params.minBalance);
    if (params.maxBalance) queryParams.append('maxBalance', params.maxBalance);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.order) queryParams.append('order', params.order);
    
    return apiClient.get(`/admin/points/users?${queryParams.toString()}`);
  },
  
  getPointsUser: (id) => apiClient.get(`/admin/points/users/${id}`),
  
  updatePointsUser: (id, data) => apiClient.patch(`/admin/points/users/${id}`, data),
  
  getPointsTransactions: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    // Add pagination and filters
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.partnerId) queryParams.append('partnerId', params.partnerId);
    if (params.ptsUserId) queryParams.append('ptsUserId', params.ptsUserId);
    if (params.type) queryParams.append('type', params.type);
    if (params.minAmount) queryParams.append('minAmount', params.minAmount);
    if (params.maxAmount) queryParams.append('maxAmount', params.maxAmount);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.order) queryParams.append('order', params.order);
    
    return apiClient.get(`/admin/points/transactions?${queryParams.toString()}`);
  },
  
  getTransactionStats: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.partnerId) queryParams.append('partnerId', params.partnerId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.groupBy) queryParams.append('groupBy', params.groupBy);
    
    return apiClient.get(`/admin/points/transactions/stats?${queryParams.toString()}`);
  },
  
  getPointsRules: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.partnerId) queryParams.append('partnerId', params.partnerId);
    if (params.actionType) queryParams.append('actionType', params.actionType);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.order) queryParams.append('order', params.order);
    
    return apiClient.get(`/admin/points/rules?${queryParams.toString()}`);
  },
  
  getPointsRule: (id) => apiClient.get(`/admin/points/rules/${id}`),
  
  createPointsRule: (rule) => apiClient.post('/admin/points/rules', rule),
  
  updatePointsRule: (id, rule) => apiClient.put(`/admin/points/rules/${id}`, rule),
  
  deletePointsRule: (id) => apiClient.delete(`/admin/points/rules/${id}`),

  // Redemption Products Management
  getRedemptionProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    return apiClient.get(`/redemption-products?${queryParams.toString()}`);
  },
  
  getRedemptionProduct: (id) => apiClient.get(`/redemption-products/${id}`),
  
  createRedemptionProduct: (data) => apiClient.post('/redemption-products', data),
  
  updateRedemptionProduct: (id, data) => apiClient.put(`/redemption-products/${id}`, data),
  
  deleteRedemptionProduct: (id) => apiClient.delete(`/redemption-products/${id}`),

  // Organizations management
  getOrganizations: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.order) queryParams.append('order', params.order);
    
    return apiClient.get(`/orgs?${queryParams.toString()}`);
  },
  
  getOrganization: (id) => apiClient.get(`/org/${id}`),
  
  createOrganization: (data) => apiClient.post('/orgs', data),
  
  updateOrganization: (id, data) => apiClient.put(`/orgs/${id}`, data),
  
  deleteOrganization: (id) => apiClient.delete(`/orgs/${id}`),

  // Organization Members management
  getOrganizationMembers: (orgId) => apiClient.get(`/org/${orgId}/members`),
  
  createMember: (orgId, data) => apiClient.post('/member', { ...data, orgId }),
  
  updateMember: (memberId, data) => apiClient.patch(`/member/${memberId}`, data),
  
  removeMember: (memberId, removedBy) => apiClient.patch(`/member/${memberId}`, { status: 'inactive', removedBy }),

  // Users management
  getUsers: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.role) queryParams.append('role', params.role);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.order) queryParams.append('order', params.order);
    
    return apiClient.get(`/users?${queryParams.toString()}`);
  },
  
  getUser: (id) => apiClient.get(`/users/${id}`),
  
  createUser: (data) => apiClient.post('/users', data),
  
  updateUser: (id, data) => apiClient.put(`/users/${id}`, data),
  
  deleteUser: (id) => apiClient.delete(`/users/${id}`),

  // Widget status
  getWidgetStatus: () => apiClient.get('/widget/status'),

  // === Plugin Admin API ===
  
  // Dashboard data
  getPluginDashboard: () => apiClient.get('/admin/plugin/dashboard'),
  
  // Partner plugin settings management
  getPartnerPluginSettings: (partnerId) => apiClient.get(`/admin/plugin/partners/${partnerId}`),
  
  updatePartnerPluginSettings: (partnerId, settings) => apiClient.put(`/admin/plugin/partners/${partnerId}`, settings),
  
  // Flow configuration management
  getFlowConfigs: () => apiClient.get('/admin/plugin/flows'),
  
  getFlowConfig: (flowConfigId) => apiClient.get(`/admin/plugin/flows/${flowConfigId}`),
  
  updateFlowConfig: (flowConfigId, config) => apiClient.put(`/admin/plugin/flows/${flowConfigId}`, config),
  
  // Plugin activity and analytics
  getPluginActivity: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.partnerId) queryParams.append('partnerId', params.partnerId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    return apiClient.get(`/admin/plugin/activity?${queryParams.toString()}`);
  },

  // === Regular API ===

  // User profile
  getUserProfile: () => apiClient.get('/user/profile'),
  
  updateUserProfile: (data) => apiClient.put('/user/profile', data),
  
  // Referral system
  trackReferral: (code) => apiClient.post('/referral/track', { code }),
  
  // Points system (for members/users)
  getPointsBalance: () => apiClient.get('/points/balance'),
  
  getPointsHistory: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.type) queryParams.append('type', params.type);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.order) queryParams.append('order', params.order);
    
    return apiClient.get(`/points/history?${queryParams.toString()}`);
  },

  // === Plugin Admin API ===
  
  // Plugin Dashboard
  getPluginDashboard: () => apiClient.get('/admin/plugin/dashboard'),
  
  // Plugin Activity
  getPluginActivity: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    return apiClient.get(`/admin/plugin/dashboard/activity?${queryParams.toString()}`);
  },
  
  // Partner Management
  getPluginPartners: () => apiClient.get('/admin/plugin/partners'),
  
  getPluginPartner: (partnerId) => apiClient.get(`/admin/plugin/partners/${partnerId}`),
  
  updatePartnerPluginSettings: (partnerId, settings) => 
    apiClient.put(`/admin/plugin/partners/${partnerId}/settings`, settings),
  
  // Flow Configuration
  getPluginFlowConfigs: () => apiClient.get('/admin/plugin/flow-configs'),
  
  updatePluginFlowConfig: (flowId, updates) => 
    apiClient.put(`/admin/plugin/flow-configs/${flowId}`, updates),
  
  // Flow Statistics
  getPluginFlowStats: () => apiClient.get('/admin/plugin/analytics/completion-rate'),

  // === Partner Admin API ===
  
  // Get all partners
  getPartners: () => apiClient.get('/admin/partners'),
  
  // Get single partner
  getPartner: (partnerId) => apiClient.get(`/admin/partners/${partnerId}`),
  
  // Update partner
  updatePartner: (partnerId, data) => apiClient.put(`/admin/partners/${partnerId}`, data),
  
  // Delete partner
  deletePartner: (partnerId) => apiClient.delete(`/admin/partners/${partnerId}`),
  
  // Get partner statistics
  getPartnerStats: (partnerId) => apiClient.get(`/admin/partners/${partnerId}/stats`),
  
  // Partner Action Configs
  getPartnerActionConfigs: (partnerId) => apiClient.get(`/admin/partners/${partnerId}/action-configs`),
  
  createPartnerActionConfig: (partnerId, data) => apiClient.post(`/admin/partners/${partnerId}/action-configs`, data),
  
  updatePartnerActionConfig: (partnerId, configId, data) => 
    apiClient.put(`/admin/partners/${partnerId}/action-configs/${configId}`, data),
  
  deletePartnerActionConfig: (partnerId, configId) => 
    apiClient.delete(`/admin/partners/${partnerId}/action-configs/${configId}`),
  
  getActionConfigActions: (configId) => apiClient.get(`/admin/action-configs/${configId}/actions`),
  
  // Partner Flow Configs
  getPartnerFlowConfigs: (partnerId) => apiClient.get(`/admin/partners/${partnerId}/flow-configs`),
  
  createPartnerFlowConfig: (partnerId, data) => apiClient.post(`/admin/partners/${partnerId}/flow-configs`, data),
  
  updatePartnerFlowConfig: (partnerId, configId, data) => 
    apiClient.put(`/admin/partners/${partnerId}/flow-configs/${configId}`, data),
  
  deletePartnerFlowConfig: (partnerId, configId) => 
    apiClient.delete(`/admin/partners/${partnerId}/flow-configs/${configId}`),
  
  reorderPartnerFlowConfig: (partnerId, configId, direction) =>
    apiClient.post(`/admin/partners/${partnerId}/flow-configs/${configId}/reorder`, { direction }),
  
  getFlowConfigFlows: (configId) => apiClient.get(`/admin/flow-configs/${configId}/flows`),
  
  // Partner Flows
  getPartnerFlows: (partnerId) => apiClient.get(`/admin/partners/${partnerId}/flows`),
  
  getFlowDetail: (flowId) => apiClient.get(`/admin/flows/${flowId}`),
  
  // Partner Actions
  getPartnerActions: (partnerId) => apiClient.get(`/admin/partners/${partnerId}/actions`),
  
  getActionConfigActions: (configId) => apiClient.get(`/admin/action-configs/${configId}/actions`),

  // === Ripple CRM API (Moved to cnxn/ripple frontend) ===
  // NOTE: These API methods are no longer needed in cnxn/frontend admin app.
  // Ripple has its own dedicated frontend at cnxn/ripple/ which handles:
  // - Member Context (Relationship History)
  // - Business Messages
  // - Intents (User Interests)
  //
  // Commented out for reference - uncomment only if cnxn/frontend needs to integrate Ripple features.
  /*
  getMemberContexts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.memberId) queryParams.append('memberId', params.memberId);
    if (params.organisationId) queryParams.append('organisationId', params.organisationId);
    if (params.status) queryParams.append('status', params.status);
    if (params.contextType) queryParams.append('contextType', params.contextType);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    return apiClient.get(`/member-context?${queryParams.toString()}`);
  },

  getMemberContext: (contextId) => apiClient.get(`/member-context/${contextId}`),

  createMemberContext: (data) => apiClient.post('/member-context', data),

  updateMemberContext: (contextId, data) => apiClient.put(`/member-context/${contextId}`, data),

  deleteMemberContext: (contextId) => apiClient.delete(`/member-context/${contextId}`),

  // Business Messages
  getBusinessMessages: (orgId) => apiClient.get(`/business-messages?organisationId=${orgId}`),

  getBusinessMessage: (messageId) => apiClient.get(`/business-messages/${messageId}`),

  createBusinessMessage: (data) => apiClient.post('/business-messages', data),

  updateBusinessMessage: (messageId, data) => apiClient.put(`/business-messages/${messageId}`, data),

  deleteBusinessMessage: (messageId) => apiClient.delete(`/business-messages/${messageId}`),

  // Intents (User Interests)
  getIntents: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.organisationId) queryParams.append('organisationId', params.organisationId);
    if (params.limit) queryParams.append('limit', params.limit);
    return apiClient.get(`/intents?${queryParams.toString()}`);
  },

  getIntent: (intentId) => apiClient.get(`/intents/${intentId}`),

  createIntent: (data) => apiClient.post('/intents', data),

  updateIntent: (intentId, data) => apiClient.put(`/intents/${intentId}`, data),

  deleteIntent: (intentId) => apiClient.delete(`/intents/${intentId}`)
  */
};